import type { StoreApi, UseBoundStore } from 'zustand';
import type { EntryType, TreeNode } from '@morph-mapper/types';
import type { Result } from 'neverthrow';
import type {
  PostRenderFn,
  BootstrapFn,
  StoreType,
  ShouldRenderFn,
  FieldSuggestionsFn,
  PreRenderFn,
  PostFieldSuggestionsFn,
  Setting,
} from '../types';

/**
 * Parameters passed from the application to the shouldRender function.
 */
export type ShouldRenderBindParams = {
  entry: TreeNode;
};

/**
 * Parameters passed from the application to the postRender function.
 */
export type PostRenderBindParams = {
  entry: TreeNode;
  template: any;
};
/**
 * Paramters passed from the application to the prerender function.
 */
export type PreRenderBindParams = {
  entries: Record<string, TreeNode>;
  textContent: string;
  recomputeDependencies: (entries: Record<string, TreeNode>) => void;
  file: File;
};

/**
 * Paramters passed from the application to the bootstrap function.
 */
export type BootStrapBindParams = {
  entries: Record<string, TreeNode>;
  textContent: string;
  recomputeDependencies: (entries: Record<string, TreeNode>) => void;
  force?: boolean;
  /** Plugins (by key) to leave out of this bootstrap. */
  exclude?: string[];
};

export type IteratorStrategy = 'table' | 'countOccurrence';

/**
 * Parameter passed from the application to the fieldSuggestions function.
 */
export type FieldSuggestionsBindParams = {
  entry: TreeNode;
  iterator?: boolean;
  iteratorStrategy?: IteratorStrategy;
  /**
   * Renders a snippet as this entry's value inside its surrounding template
   * (iterators included), returning what the entry would actually output.
   * `callRender` renders against the whole document without that context.
   */
  renderInContext?: (
    code: string,
  ) => Promise<Result<unknown, PluginFrameworkError>>;
};
export type PostFieldSuggestionsBindParams = {
  entry: TreeNode;
  suggestions: FieldSuggestion[] | void;
  iteratorStrategy?: IteratorStrategy;
};

/**
 * Parameters returned from the Plugin framework to the application.
 */
// TODO?: Should map types be usable?
export type FieldSuggestion = {
  value: unknown;
  snippet: string;
  type: EntryType;
  iterator?: boolean;
  source: string;
  autofill?: boolean;
  expectedValue?: unknown;
};
export type FieldSuggestionsReturnParams =
  | Omit<FieldSuggestion, 'source'>[]
  | void;

/**
 * The interface that is exposed to the plugin implementation. This contains all functions and fields that have to be
 * provided by the plugin.
 */
export interface PluginHooks {
  shouldRender: ShouldRenderFn;
  postRender: PostRenderFn;
  bootstrap: BootstrapFn;
  preRender: PreRenderFn;
  fieldSuggestions: FieldSuggestionsFn;
  postFieldSuggestions: PostFieldSuggestionsFn;

  name: string;
  version: string;
  description?: string;
  settings?: Setting[];
  _store: UseBoundStore<StoreApi<StoreType<any>>>;
}

/**
 * Error type used by the plugin framework to communicate errors to a plugin.
 */
export type PluginFrameworkError = {
  type: PluginFrameworkErrorType;
  message: string;
};

/**
 * Error types used by the plugin framework to communicate errors to a plugin.
 */
export enum PluginFrameworkErrorType {
  INVALID_PATH,
  PATH_NOT_FOUND,
  CORRUPTED_STATE,
  KEY_ALREADY_EXISTS,
  NOT_IMPLEMENTED,
  INVALID_ARGUMENTS,
  KEY_IN_USE,
}
